// Типи для instances від Ant Design App.useApp()
type MessageInstance = any
type NotificationInstance = any
type ModalInstance = any

/**
 * Сервіс нотифікацій для роботи з Ant Design Notification поза компонентами
 *
 * Дозволяє використовувати нотифікації Ant Design в сервісах, менеджерах, задачах
 * та звичайних функціях, де немає доступу до хуків React.
 *
 * @example
 * ```ts
 * import { notificationService } from '@/Core/Services/NotificationService'
 *
 * notificationService.showSuccess('Успіх', 'Операція виконана успішно')
 * notificationService.showError('Помилка', 'Щось пішло не так')
 * ```
 */
export class NotificationService {
    private notification: NotificationInstance | null = null
    private message: MessageInstance | null = null
    private modal: ModalInstance | null = null
    private loaderInstance: ReturnType<MessageInstance['loading']> | null = null

    /**
     * Ініціалізація сервісу з notification, message та modal instances від Ant Design
     * Викликається автоматично в Layout компоненті через App.useApp()
     */
    initialize(notification: NotificationInstance, message: MessageInstance, modal: ModalInstance): void {
        this.notification = notification
        this.message = message
        this.modal = modal
    }

    /**
     * Показати повідомлення про помилку
     *
     * @param message - Заголовок повідомлення
     * @param description - Опис помилки
     * @throws Error якщо сервіс не ініціалізовано
     */
    showError(message?: string, description?: string): void {
        if (!this.notification) {
            console.error('NotificationService не ініціалізовано! Переконайтеся, що викликано initialize()')
            return
        }

        this.notification.error({
            duration: 10,
            showProgress: true,
            pauseOnHover: true,
            title: message || 'Помилка',
            description: description || 'Сталася помилка. Спробуйте ще раз пізніше.',
            placement: 'bottomRight',
        })
    }

    /**
     * Показати повідомлення про успіх
     *
     * @param message - Заголовок повідомлення
     * @param description - Опис успішної операції
     * @throws Error якщо сервіс не ініціалізовано
     */
    showSuccess(message?: string, description?: string): void {
        if (!this.notification) {
            console.error('NotificationService не ініціалізовано! Переконайтеся, що викликано initialize()')
            return
        }

        this.notification.success({
            duration: 5,
            showProgress: true,
            pauseOnHover: true,
            title: message || 'Успіх',
            description: description || 'Операція виконана успішно.',
            placement: 'bottomRight',
        })
    }

    /**
     * Показати інформаційне повідомлення
     *
     * @param message - Заголовок повідомлення
     * @param description - Опис
     */
    showInfo(message?: string, description?: string): void {
        if (!this.notification) {
            console.error('NotificationService не ініціалізовано!')
            return
        }

        this.notification.info({
            duration: 5,
            showProgress: true,
            pauseOnHover: true,
            title: message || 'Інформація',
            description: description || '',
            placement: 'bottomRight',
        })
    }

    /**
     * Показати попередження
     *
     * @param message - Заголовок повідомлення
     * @param description - Опис попередження
     */
    showWarning(message?: string, description?: string): void {
        if (!this.notification) {
            console.error('NotificationService не ініціалізовано!')
            return
        }

        this.notification.warning({
            duration: 7,
            showProgress: true,
            pauseOnHover: true,
            title: message || 'Попередження',
            description: description || '',
            placement: 'bottomRight',
        })
    }

    /**
     * Отримати об'єкт message від Ant Design для прямого використання
     *
     * @example
     * ```ts
     * const message = notificationService.getMessage()
     * message.success('Збережено')
     * message.error('Помилка')
     * message.loading('Завантаження...', 0)
     * ```
     */
    getMessage(): MessageInstance | null {
        if (!this.message) {
            console.error('NotificationService не ініціалізовано! Переконайтеся, що викликано initialize()')
        }
        return this.message
    }

    /**
     * Отримати об'єкт notification від Ant Design для прямого використання
     *
     * @example
     * ```ts
     * const notification = notificationService.getNotification()
     * notification.open({
     *   title: 'Заголовок',
     *   description: 'Опис',
     *   icon: <Icon />
     * })
     * ```
     */
    getNotification(): NotificationInstance | null {
        if (!this.notification) {
            console.error('NotificationService не ініціалізовано! Переконайтеся, що викликано initialize()')
        }
        return this.notification
    }

    /**
     * Показати глобальний лоадер
     *
     * @param content - Текст лоадера (за замовчуванням 'Завантаження...')
     * @param duration - Тривалість в секундах (0 = безкінечно)
     *
     * @example
     * ```ts
     * notificationService.showLoader('Завантаження даних...')
     * await fetchData()
     * notificationService.hideLoader()
     * ```
     */
    showLoader(content: string = 'Завантаження...', duration: number = 0): void {
        if (!this.message) {
            console.error('NotificationService не ініціалізовано! Переконайтеся, що викликано initialize()')
            return
        }

        // Якщо вже є активний лоадер, закриваємо його
        if (this.loaderInstance) {
            this.loaderInstance()
            this.loaderInstance = null
        }

        // Показуємо новий лоадер
        this.loaderInstance = this.message.loading(content, duration)
    }

    /**
     * Сховати глобальний лоадер
     *
     * @example
     * ```ts
     * notificationService.showLoader()
     * await fetchData()
     * notificationService.hideLoader()
     * ```
     */
    hideLoader(): void {
        if (this.loaderInstance) {
            this.loaderInstance()
            this.loaderInstance = null
        }
    }

    /**
     * Перевірка чи сервіс готовий до роботи
     */
    isReady(): boolean {
        return this.notification !== null && this.message !== null && this.modal !== null
    }

    /**
     * Закрити всі нотифікації
     */
    destroyAll(): void {
        if (!this.notification) {
            console.error('NotificationService не ініціалізовано!')
            return
        }

        this.notification.destroy()
    }
}

/**
 * Singleton екземпляр NotificationService
 */
const _notificationService = new NotificationService()

/**
 * Фабрична функція для створення NotificationService
 */
export function createNotificationService(): NotificationService {
    return new NotificationService()
}

/**
 * Глобальний екземпляр NotificationService
 */
export const notificationService = _notificationService
